import { extent, max, min } from 'd3-array';
import { select } from 'd3-selection';
import BarChartIcon from '@material-ui/icons/BarChart';
import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { PADDING_CHARTS } from '../../constants/chart-options';
import DotPlotChart from './dot-plot-chart';
import ErrorMessage from '../../../common/components/error/error-message';
import LoadingBar from '../../../common/components/loading-bar';
import StreamGraphChart from './stream-graph-chart';
import * as chartUtils from '../../../utils/chart-utils';
import styles from './stream-dot-plot-chart.module.scss';


class StreamDotPlotChart extends Component{
    constructor( props ){
        super( props );
        const { options: { height, margin, width } } = this.props;

        this.state = {
            innerHeight: chartUtils.getInnerDimension( height, margin.top, margin.bottom ) - PADDING_CHARTS,
            innerWidth: chartUtils.getInnerDimension( width, margin.right, margin.left ),
        };
    }

    /* Scales should typically be defined in StreamGraph/DotPlot
     * However, in order to plot the interpolation line b/w dot plot & stream graph,
     * we need access to all four scales
     */
    xScale = () => {
        const { data, xKey } = this.props;
        const xExtent = extent( data, d => d[xKey] );
        let xPaddedMin = chartUtils.padDate( 'subtract', xExtent[0], xExtent );
        let xPaddedMax = chartUtils.padDate( 'add', xExtent[1], xExtent );

        return chartUtils.generateScale(
            'time',
            [ xPaddedMin, xPaddedMax ],
            [ 0, this.state.innerWidth ]
        );
    };

    dotYScale = () => {
        return chartUtils.generateScale(
            'linear',
            [ 0, 100 ],
            [ this.state.innerHeight * 3 / 4, 0 ]
        );
    };

    streamYScale = () => chartUtils.generateScale(
        'linear',
        [ min( this.streamLayer(), chartUtils.getStackMin ), max( this.streamLayer(), chartUtils.getStackMax ) ],
        [ this.state.innerHeight / 4, 0 ]
    ).nice();

    streamLayer = () => chartUtils.generateStack( this.props.streamOptions.keys )( this.props.data );

    /* Helper to position vertical lines such that they center on the grid lines.
     */
    vertLineXPos = ( d ) => {
        return this.xScale()( d.date ) + 0.4;
    }

    /* Because the stream is opaque, the upper y-coord of the vertical interpolation can just be
     * equivalent to the baseline of the curve.
     */
    renderVerticalInterpolation = () => {
        const { data } = this.props;
        const { innerHeight } = this.state;
        const verticalLines = this.interpolation;
        const baseline = innerHeight / 8;

        select( verticalLines )
            .selectAll( 'line' )
            .data( data )
            .enter()
            .append( 'line' )
            .attr( 'x1', this.vertLineXPos )
            .attr( 'x2', this.vertLineXPos )
            .attr( 'y1', baseline )
            .attr( 'y2', innerHeight / 4 + PADDING_CHARTS );
    }

    render(){
        const {
            data,
            isLoading,
            interval,
            dotPlotOptions: {
                key: dotYKey
            },
            streamOptions: {
                keys
            },
            xKey,
            options: {
                height,
                margin,
                width
            }
        } = this.props;
        const { innerHeight, innerWidth } = this.state;

        if( isLoading ){
            return <LoadingBar />;
        }
        else if( !isLoading && chartUtils.dataIsZero( data, keys ) ){
            return <ErrorMessage icon={<BarChartIcon />} />;
        }
        else{
            return (
                <div className={styles.streamDotPlotChart}>
                    <canvas className={styles.canvas} width={`${width}`} height={`${height}`}></canvas>
                    <svg
                        className={styles.chart}
                        preserveAspectRatio="xMidYMid meet"
                        viewBox={`0 0 ${width} ${height + 20}`}
                    >
                        <g transform={`translate(${margin.left}, ${margin.top * 2})`}>
                            <g className={styles.interpolation}
                                data-rendered={true}
                                ref={el => this.interpolation = el}>
                            </g>
                            <StreamGraphChart
                                data={data}
                                height={innerHeight / 4}
                                interval={interval}
                                layer={this.streamLayer}
                                margin={margin}
                                scales={{
                                    xScale: this.xScale,
                                    yScale: this.streamYScale
                                }}
                                width={innerWidth}
                                xKey={xKey}
                                yKey={dotYKey}
                                topOffset={innerHeight * 3 / 4 + PADDING_CHARTS}
                            />
                            <DotPlotChart
                                data={data}
                                height={innerHeight * 3 / 4}
                                interval={interval}
                                margin={margin}
                                scales={{
                                    xScale: this.xScale,
                                    yScale: this.dotYScale
                                }}
                                styles={styles}
                                width={innerWidth}
                                yKey={dotYKey}
                                xKey={xKey}
                            />
                        </g>
                    </svg>
                </div>
            );
        }
    }
};

StreamDotPlotChart.propTypes = {
    data: PropTypes.array.isRequired,
    dotPlotOptions: PropTypes.object.isRequired,
    interval: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    options: PropTypes.shape( {
        height: PropTypes.number,
        margin: PropTypes.object,
        width: PropTypes.number,
    } ),
    streamOptions: PropTypes.object.isRequired,
    xKey: PropTypes.string.isRequired,
};

export default StreamDotPlotChart;
